import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';

interface LookupGroup {
  id: string;
  groupCode: string;
  groupName: string;
  description: string;
  isSystem: boolean;
  isActive: boolean;
}

interface LookupValue {
  id: string;
  groupId: string;
  code: string;
  displayName: string;
  description: string;
  sortOrder: number;
  isSystem: boolean;
  isActive: boolean;
}

const emptyGroupForm = { groupCode: '', groupName: '', description: '' };
const emptyValueForm = { code: '', displayName: '', description: '', sortOrder: '0' };

export function LookupsPage() {
  const [groups, setGroups] = useState<LookupGroup[]>([]);
  const [values, setValues] = useState<LookupValue[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<LookupGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [valLoading, setValLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<LookupGroup | null>(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);

  // Value modal state
  const [showValueModal, setShowValueModal] = useState(false);
  const [editingValue, setEditingValue] = useState<LookupValue | null>(null);
  const [valueForm, setValueForm] = useState(emptyValueForm);
  const [valueSaving, setValueSaving] = useState(false);
  const [valueError, setValueError] = useState<string | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet<LookupGroup[]>('/admin/lookups');
      setGroups(data);
      if (data.length > 0 && !selectedGroup) setSelectedGroup(data[0]);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedGroup]);

  const loadValues = useCallback(async (groupId: string) => {
    setValLoading(true);
    try {
      const data = await apiGet<LookupValue[]>(`/admin/lookups/${groupId}/values`);
      setValues(data);
    } finally {
      setValLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => {
    if (selectedGroup) loadValues(selectedGroup.id);
  }, [selectedGroup, loadValues]);

  // ── Group CRUD ──────────────────────────────────────────────────────────
  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm(emptyGroupForm);
    setGroupError(null);
    setShowGroupModal(true);
  };

  const openEditGroup = (g: LookupGroup) => {
    setEditingGroup(g);
    setGroupForm({ groupCode: g.groupCode, groupName: g.groupName, description: g.description ?? '' });
    setGroupError(null);
    setShowGroupModal(true);
  };

  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGroupSaving(true);
    setGroupError(null);
    try {
      if (editingGroup) {
        const updated = await apiPut<LookupGroup>(`/admin/lookups/${editingGroup.id}`, {
          groupName: groupForm.groupName,
          description: groupForm.description,
        });
        setGroups((prev) => prev.map((g) => g.id === editingGroup.id ? { ...g, ...updated } : g));
        if (selectedGroup?.id === editingGroup.id) setSelectedGroup((s) => s ? { ...s, ...updated } : s);
      } else {
        const created = await apiPost<LookupGroup>('/admin/lookups', {
          groupCode: groupForm.groupCode,
          groupName: groupForm.groupName,
          description: groupForm.description,
        });
        setGroups((prev) => [...prev, created]);
        setSelectedGroup(created);
      }
      setShowGroupModal(false);
    } catch (e) {
      setGroupError(String(e));
    } finally {
      setGroupSaving(false);
    }
  };

  // ── Value CRUD ──────────────────────────────────────────────────────────
  const openNewValue = () => {
    setEditingValue(null);
    setValueForm(emptyValueForm);
    setValueError(null);
    setShowValueModal(true);
  };

  const openEditValue = (v: LookupValue) => {
    setEditingValue(v);
    setValueForm({ code: v.code, displayName: v.displayName, description: v.description ?? '', sortOrder: String(v.sortOrder) });
    setValueError(null);
    setShowValueModal(true);
  };

  const handleValueSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup) return;
    setValueSaving(true);
    setValueError(null);
    try {
      if (editingValue) {
        await apiPut(`/admin/lookups/${selectedGroup.id}/values/${editingValue.id}`, {
          code: valueForm.code.toUpperCase(),
          displayName: valueForm.displayName,
          description: valueForm.description,
          sortOrder: Number(valueForm.sortOrder),
        });
        setValues((prev) =>
          prev.map((v) =>
            v.id === editingValue.id
              ? { ...v, code: valueForm.code.toUpperCase(), displayName: valueForm.displayName, description: valueForm.description, sortOrder: Number(valueForm.sortOrder) }
              : v,
          ),
        );
      } else {
        await apiPost(`/admin/lookups/${selectedGroup.id}/values`, {
          code: valueForm.code.toUpperCase(),
          displayName: valueForm.displayName,
          description: valueForm.description,
          sortOrder: Number(valueForm.sortOrder),
        });
        await loadValues(selectedGroup.id);
      }
      setShowValueModal(false);
    } catch (e) {
      setValueError(String(e));
    } finally {
      setValueSaving(false);
    }
  };

  const handleToggleValue = async (val: LookupValue) => {
    await apiPut(`/admin/lookups/${selectedGroup!.id}/values/${val.id}`, { isActive: !val.isActive });
    setValues((prev) => prev.map((v) => v.id === val.id ? { ...v, isActive: !v.isActive } : v));
  };

  const setGF = (k: keyof typeof groupForm, v: string) => setGroupForm((f) => ({ ...f, [k]: v }));
  const setVF = (k: keyof typeof valueForm, v: string) => setValueForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="flex h-full">
      {/* ── Sidebar ── */}
      <aside className="flex w-64 flex-col border-r border-gray-200 bg-white overflow-hidden">
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-700">Groups</h2>
          <Button size="sm" onClick={openNewGroup}>+ New</Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <PageSpinner />}
          {groups.map((g) => (
            <div
              key={g.id}
              className={`group flex items-start justify-between border-b border-gray-100 px-4 py-2.5 cursor-pointer transition-colors ${
                selectedGroup?.id === g.id ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => setSelectedGroup(g)}
            >
              <div className="min-w-0">
                <p className={`font-mono text-xs font-semibold truncate ${selectedGroup?.id === g.id ? 'text-blue-700' : 'text-gray-700'}`}>
                  {g.groupCode}
                </p>
                <p className="text-xs text-gray-500 truncate">{g.groupName}</p>
              </div>
              {!g.isSystem && (
                <button
                  onClick={(e) => { e.stopPropagation(); openEditGroup(g); }}
                  className="ml-2 shrink-0 rounded p-0.5 text-gray-300 opacity-0 group-hover:opacity-100 hover:text-blue-600 hover:bg-blue-50"
                  title="Edit group"
                >
                  ✎
                </button>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* ── Values panel ── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {error && <p className="p-4 text-red-600">{error}</p>}

        {selectedGroup && (
          <>
            <div className="flex shrink-0 items-start justify-between border-b px-6 py-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{selectedGroup.groupName}</h1>
                <p className="mt-0.5 text-sm text-gray-500">{selectedGroup.description}</p>
              </div>
              <div className="flex gap-2">
                {!selectedGroup.isSystem && (
                  <Button size="sm" variant="secondary" onClick={() => openEditGroup(selectedGroup)}>Edit Group</Button>
                )}
                {!selectedGroup.isSystem && (
                  <Button size="sm" onClick={openNewValue}>+ Add Value</Button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {valLoading && <PageSpinner />}
              {!valLoading && (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Code', 'Display Name', 'Description', 'Sort', 'Active', ''].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {values.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No values yet. Add the first one.</td></tr>
                      )}
                      {values.map((v) => (
                        <tr key={v.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-mono text-sm font-medium text-gray-900">{v.code}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{v.displayName}</td>
                          <td className="px-4 py-2 text-sm text-gray-400 max-w-xs truncate">{v.description}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{v.sortOrder}</td>
                          <td className="px-4 py-2">
                            <span className={`text-xs font-medium ${v.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                              {v.isActive ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            {!v.isSystem && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="secondary" onClick={() => openEditValue(v)}>Edit</Button>
                                <Button size="sm" variant="ghost" onClick={() => handleToggleValue(v)}>
                                  {v.isActive ? 'Disable' : 'Enable'}
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Group modal ── */}
      <Modal
        open={showGroupModal}
        title={editingGroup ? `Edit Group — ${editingGroup.groupCode}` : 'New Lookup Group'}
        onClose={() => { setShowGroupModal(false); setGroupError(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowGroupModal(false)}>Cancel</Button>
            <Button form="group-form" type="submit" loading={groupSaving}>{editingGroup ? 'Save' : 'Create'}</Button>
          </>
        }
      >
        <form id="group-form" onSubmit={handleGroupSubmit} className="space-y-4">
          <Input
            label="Group Code (unique, uppercase)"
            value={groupForm.groupCode}
            onChange={(e) => setGF('groupCode', e.target.value.toUpperCase().replace(/\s+/g, '_'))}
            placeholder="MY_GROUP"
            required={!editingGroup}
            disabled={!!editingGroup}
          />
          <Input
            label="Group Name"
            value={groupForm.groupName}
            onChange={(e) => setGF('groupName', e.target.value)}
            placeholder="e.g. Priority Levels"
            required
          />
          <Textarea
            label="Description (optional)"
            value={groupForm.description}
            onChange={(e) => setGF('description', e.target.value)}
          />
          {groupError && <p className="text-sm text-red-600">{groupError}</p>}
        </form>
      </Modal>

      {/* ── Value modal ── */}
      <Modal
        open={showValueModal}
        title={editingValue ? `Edit Value — ${editingValue.code}` : `New Value in ${selectedGroup?.groupCode}`}
        onClose={() => { setShowValueModal(false); setValueError(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowValueModal(false)}>Cancel</Button>
            <Button form="value-form" type="submit" loading={valueSaving}>{editingValue ? 'Save' : 'Add'}</Button>
          </>
        }
      >
        <form id="value-form" onSubmit={handleValueSubmit} className="space-y-4">
          <Input
            label="Code (uppercase)"
            value={valueForm.code}
            onChange={(e) => setVF('code', e.target.value.toUpperCase())}
            placeholder="MY_VALUE"
            required
          />
          <Input
            label="Display Label"
            value={valueForm.displayName}
            onChange={(e) => setVF('displayName', e.target.value)}
            required
          />
          <Textarea
            label="Description (optional)"
            value={valueForm.description}
            onChange={(e) => setVF('description', e.target.value)}
          />
          <Input
            label="Sort Order"
            type="number"
            value={valueForm.sortOrder}
            onChange={(e) => setVF('sortOrder', e.target.value)}
          />
          {valueError && <p className="text-sm text-red-600">{valueError}</p>}
        </form>
      </Modal>
    </div>
  );
}
